import React, { useMemo } from 'react';
import {Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import PartnerDashboardShell from '../components/PartnerDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import { getSchoolStatus } from '../../../utils/dashboardStatus';
import { DEPLOYMENT_ACTION_BAND_COLORS } from '../constants';

import {BandChip} from '../../DashboardCommon/widgets.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';
const NAVY = '#052F44';

/* ── Status chip config ─────────────────────────────────────────── */
const getStatusColor = (label) => {
    return DEPLOYMENT_ACTION_BAND_COLORS[label];
};

const StatusChip = ({ label }) => <BandChip label={label} color={getStatusColor(label) || '#6B7280'}/>;


/* ── Helper to dynamically decide status, happening, and action ── */
export const analyzeRowData = (rowRaw) => {
    const client = rowRaw.client;
    const country = rowRaw.country;
    const anyRisk = rowRaw.anyRisk;
    const coverage = rowRaw.coverage;
    const assessed = rowRaw.assessed;
    const periodEnd = rowRaw.periodEnd;

    let daysSinceLastActivity = null;
    if (periodEnd) {
        const lastActiveDate = new Date(periodEnd);
        const today = new Date();
        const diffTime = today - lastActiveDate;
        if (!isNaN(diffTime)) {
            daysSinceLastActivity = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        }
    }

    const status = getSchoolStatus(rowRaw);

    let happening = '';
    let action = '';

    if (status === 'High risk') {
        const riskText = anyRisk != null ? ` (${anyRisk}%)` : '';
        happening = `A priority concern appears in the risk profile${riskText}`;
        action = 'Safeguarding support takes precedence';
    } else if (status === 'Low coverage') {
        const coverageText = coverage != null ? ` (${coverage}%)` : '';
        happening = `Participation sits below the reporting threshold${coverageText}`;
        action = 'Interpret comparisons with caution';
    } else if (status === 'Data weak') {
        const countText = assessed != null ? ` (${assessed} assessed)` : '';
        happening = `The assessed sample is too small${countText}`;
        action = 'Collect more data to support confident reporting';
    } else if (status === 'Dormant') {
        const months = daysSinceLastActivity != null ? Math.round(daysSinceLastActivity / 30) : null;
        const timeText = months != null && months > 0 ? ` (${months} months)` : '';
        happening = `Participation is minimal with no recent activity${timeText}`;
        action = 'Reengage or archive the deployment';
    } else if (status === 'Active') {
        happening = 'Deployment is running with adequate coverage and no priority concern';
        action = 'Results are ready for use';
    } else if (status === 'Growth') {
        happening = 'Coverage and composite results are both strong';
        action = 'Deployment is positioned to expand';
    }

    return {
        client,
        country,
        status,
        happening,
        action
    };
};


/* ── Header cell styles ─────────────────────────────────────────── */
const headCell = {
    backgroundColor: NAVY,
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 700,
    borderBottom: 'none',
    py: 2,
    px: 2.5,
};

/* ── The table (QA 9 Oct 2026: every column sorts and filters from its header) ── */
// getSchoolStatus's own order, best first; "Growth Opportunity" is the full name of "Growth".
const STATUS_ORDER = ['Growth', 'Growth Opportunity', 'Active', 'High risk', 'Low coverage', 'Data weak', 'Dormant'];
const COLUMNS = [
    {key: 'client', label: 'School', type: 'text'},
    {key: 'country', label: 'Country', type: 'text'},
    {key: 'status', label: 'Status', type: 'band', order: STATUS_ORDER},
    // Sentences built per row (with the row's own figures) — they sort, but are too varied to filter.
    {key: 'happening', label: 'What is happening', type: 'text', filterable: false},
    {key: 'action', label: 'Recommended action', type: 'text', filterable: false},
];
const cellBase = {py: 2, px: 2.5, borderBottom: '1px solid #F3F4F6'};

// The table sits in its own component so the sort/filter hook runs below the page's loading branches.
const AttentionTable = ({rows}) => {
    const t = useTableControls(rows, COLUMNS);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('client')} sx={{...headCell, width: '20%'}}/>
                            <SortFilterHeaderCell {...t.headerProps('country')} sx={{...headCell, width: '13%'}}/>
                            <SortFilterHeaderCell {...t.headerProps('status')} sx={{...headCell, width: '14%'}}/>
                            <SortFilterHeaderCell {...t.headerProps('happening')} sx={{...headCell, width: '28%'}}/>
                            <SortFilterHeaderCell {...t.headerProps('action')} sx={{...headCell, width: '25%'}}/>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} sx={{...cellBase, textAlign: 'center', py: 4, fontSize: '13px', color: '#6A7282'}}>No school matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, i) => (
                            <TableRow
                                key={row.client + '-' + rows.indexOf(row)}
                                sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}
                            >
                                <TableCell sx={{...cellBase, fontSize: '14px', fontWeight: 700, color: NAVY}}>
                                    {row.client}
                                </TableCell>
                                <TableCell sx={{...cellBase, fontSize: '14px', color: '#374151'}}>
                                    {row.country}
                                </TableCell>
                                <TableCell sx={cellBase}>
                                    <StatusChip label={row.status}/>
                                </TableCell>
                                <TableCell sx={{...cellBase, fontSize: '13px', color: '#374151', lineHeight: 1.6}}>
                                    {row.happening}
                                </TableCell>
                                <TableCell sx={{...cellBase, fontSize: '13px', color: '#374151', lineHeight: 1.6}}>
                                    {row.action}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

/* ── Main page ──────────────────────────────────────────────────── */
const ATTENTION_NEEDED_SECTION_CODE = 'S_5';
const CARD_SECTIONS = [ATTENTION_NEEDED_SECTION_CODE];

const AttentionNeededPage = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });

    const s5Section = useMemo(() => {
        return dashboardData?.cards?.sections?.find(s => s.section_code === ATTENTION_NEEDED_SECTION_CODE);
    }, [dashboardData]);

    const titleCard = useMemo(() => {
        return s5Section?.cards?.find(c => c.card_type === 'TitleCard');
    }, [s5Section]);

    const tableCard = useMemo(() => {
        return s5Section?.cards?.find(c => c.card_type === 'TableCard');
    }, [s5Section]);

    const rows = useMemo(() => {
        const tableObjects = tableCard?.table_objects || [];
        if (!tableObjects.length) {
            return [];
        }

        const partnerCol = tableObjects.find(c => c.column_name === 'Partner' || c.column_name === 'Client');
        const countryCol = tableObjects.find(c => c.column_name === 'Country');
        const compositeCol = tableObjects.find(c => c.column_name === 'Composite');
        const anyRiskCol = tableObjects.find(c => c.column_name === 'Any-risk' || c.column_name === 'Any Risk');
        const coverageCol = tableObjects.find(c => c.column_name === 'Participation %' || c.column_name === 'Coverage %' || c.column_name === 'Coverage');
        const assessedCol = tableObjects.find(c => c.column_name === 'Assessed');

        const dormantCol = tableObjects.find(c => c.column_name === 'Dormant' || c.column_name === 'Dormant Status');
        const dataWeakCol = tableObjects.find(c => c.column_name === 'Data Weak' || c.column_name === 'Data Weak Status');
        const lowCoverageCol = tableObjects.find(c => c.column_name === 'Low Participation' || c.column_name === 'Low Participation Status' || c.column_name === 'Low Coverage');
        const atRiskCol = tableObjects.find(c => c.column_name === 'At Risk' || c.column_name === 'At Risk Status' || c.column_name === 'High Risk');
        const activeCol = tableObjects.find(c => c.column_name === 'Active' || c.column_name === 'Active Status');
        const growthCol = tableObjects.find(c => c.column_name === 'Growth Opportunity' || c.column_name === 'Growth Opportunity Status');

        const referenceCol = partnerCol || countryCol || tableObjects[0];

        return referenceCol.values.map((val, i) => {
            const rawRow = {
                client: partnerCol?.values?.[i],
                country: countryCol?.values?.[i],
                composite: compositeCol?.values?.[i],
                anyRisk: anyRiskCol?.values?.[i],
                coverage: coverageCol?.values?.[i],
                assessed: assessedCol?.values?.[i],
                periodEnd: coverageCol?.cell_data?.[i]?.period_end || assessedCol?.cell_data?.[i]?.period_end,

                dormant: dormantCol?.values?.[i],
                dataWeak: dataWeakCol?.values?.[i],
                lowCoverage: lowCoverageCol?.values?.[i],
                highRisk: atRiskCol?.values?.[i],
                active: activeCol?.values?.[i],
                growth: growthCol?.values?.[i],
            };
            return analyzeRowData(rawRow);
        });
    }, [tableCard]);


    const headerTitle = titleCard?.title;
    const headerSubtitle = titleCard?.footer;

    return (
        <PartnerDashboardShell>

            {/* Title */}
            <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, mb: 1}}>
                {headerTitle}
            </Typography>

            {/* Subtitle */}
            {headerSubtitle && (
                <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#4B5563', mb: 3}}>
                    {headerSubtitle}
                </Typography>
            )}

            {loading ? (
                <CircularLoader />
            ) : (!s5Section || rows.length === 0) ? (
                <NoDataAvailable />
            ) : (
                <AttentionTable rows={rows}/>
            )}

        </PartnerDashboardShell>
    );
};

export default AttentionNeededPage;
